<template>
    <div>

        <Loader
            :visible="isLoading"
            :text="loadingText"
            :processed="validationProgress.processed"
            :total="validationProgress.total"
        />

        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <div class="p-6 pb-0">
            <div
                @dragover.prevent="dragOver = true"
                @dragleave.prevent="dragOver = false"
                @drop.prevent="handleFileDrop"
                :class="[
                    'relative border-2 border-dashed rounded-lg p-12 transition-all duration-200 text-center',
                    dragOver ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-white hover:border-green-400'
                ]"
            >
                <input
                    type="file"
                    ref="fileInput"
                    @change="handleFileSelect"
                    accept=".xlsx, .csv"
                    class="hidden"
                />

                <div class="flex flex-col items-center justify-center space-y-4">
                    <div class="p-3 bg-green-100 rounded-full">
                        <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                        </svg>
                    </div>

                    <div class="text-gray-600">
                        <p class="text-lg font-semibold">
                            {{ selectedFile ? selectedFile.name : $t('gm.carica_excel.drag_hint') }}
                        </p>
                        <p class="text-sm">{{ $t('gm.carica_excel.click_hint') }}</p>
                    </div>
                    
                    <button
                        v-if="!selectedFile"
                        type="button"
                        @click="$refs.fileInput.click()"
                        class="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-md transition-colors shadow-sm"
                        
                    >
                        {{ $t('gm.carica_excel.select_file') }}
                    </button>
                    <template v-if="!selectedFile">
                        <p class="text-sm text-gray-500">formato accettato: .xlsx .csv</p>
                    </template>
                    <div v-else class="flex gap-2">
                        <template v-if="!validated">
                            <button
                                @click="elaboraExcel(selectedFile)"
                                :disabled="isLoading"
                                class="bg-green-600 text-white px-6 py-2 rounded-md font-medium disabled:opacity-50"
                            >
                                {{ $t('gm.carica_excel.upload_file') }}
                            </button>
                        </template>

                        <template v-else>
                            <button
                                @click="resetSelection"
                                class="bg-yellow-200 text-gray-700 px-6 py-2 rounded-md hover:bg-yellow-300"
                            >
                                {{ $t('gm.carica_excel.upload_other_file') }}
                            </button>
                        </template>

                        <button
                            @click="resetSelection"
                            class="bg-gray-200 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-300"
                        >
                            {{ $t('gm.carica_excel.cancel') }}
                        </button>
                    </div>
                </div>

                <div
                    v-if="dragOver"
                    class="absolute inset-0 bg-green-500 bg-opacity-10 pointer-events-none rounded-lg"
                ></div>
            </div>

            <div
                v-if="excelData.rows.length"
                class="mt-6 mb-6 p-6 overflow-x-auto bg-white rounded-md shadow-lg"
            >
                <div v-if="validationResult" class="mb-4 flex gap-4 items-center flex-wrap">
                    <span
                        v-if="validationResult.has_blocking_errors"
                        class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700"
                    >
                        ❌ {{ validationResult.summary.blocking_errors_count }} riga/e con errori bloccanti
                    </span>

                    <span
                        v-if="validationResult.summary.warnings_count > 0"
                        class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-700"
                    >
                        ⚠️ {{ validationResult.summary.warnings_count }} warning
                    </span>

                    <span
                        v-if="!validationResult.has_blocking_errors && validationResult.summary.warnings_count === 0"
                        class="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700"
                    >
                        ✅ Tutte le righe sono valide
                    </span>

                    <button
                        @click="validateExcelData"
                        :disabled="validating"
                        class="ml-auto text-sm px-4 py-1 border border-blue-400 text-blue-600 rounded-md hover:bg-blue-50 disabled:opacity-50"
                    >
                        {{ validating ? 'Validazione in corso...' : 'Rivalida dati' }}
                    </button>
                </div>

                <div v-if="validating && !validationResult" class="mb-4 text-sm text-gray-500 italic">
                    Validazione in corso...
                </div>

                <table class="min-w-full border-collapse">
                    <thead>
                        <tr class="bg-gray-100">
                            <th class="py-3 px-4 border-b text-left text-xs font-semibold uppercase tracking-wider text-gray-700 w-12">
                                Stato
                            </th>

                            <th
                                v-for="header in excelData.headers"
                                :key="header"
                                class="py-3 px-4 border-b text-left text-xs font-semibold uppercase tracking-wider text-gray-700"
                            >
                                {{ header }}
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        <template v-for="(row, index) in excelData.rows" :key="index">
                            <tr :class="getRowClass(index)">
                                <td class="py-2 px-4 border-b text-center text-lg">
                                    {{ getRowStatusIcon(index) }}
                                </td>

                                <td
                                    v-for="header in excelData.headers"
                                    :key="header"
                                    class="py-2 px-4 border-b text-sm text-gray-600"
                                >
                                    {{ row[header] }}
                                </td>
                            </tr>

                            <tr v-if="hasRowDetails(index)" :class="getRowDetailClass(index)">
                                <td :colspan="excelData.headers.length + 1" class="px-6 pb-3 pt-0 border-b">
                                    <div class="flex flex-wrap gap-6 text-xs">
                                        <div v-if="getRowValidation(index)?.errors?.length" class="text-red-700">
                                            <p class="font-semibold mb-1">Errori bloccanti:</p>

                                            <ul class="list-disc list-inside space-y-0.5">
                                                <li
                                                    v-for="(e, i) in getRowValidation(index).errors"
                                                    :key="i"
                                                >
                                                    <span class="font-medium">{{ e.field }}:</span> {{ e.message }}
                                                </li>
                                            </ul>
                                        </div>

                                        <div v-if="getRowValidation(index)?.warnings?.length" class="text-yellow-700">
                                            <p class="font-semibold mb-1">Warning:</p>

                                            <ul class="list-disc list-inside space-y-0.5">
                                                <li
                                                    v-for="(w, i) in getRowValidation(index).warnings"
                                                    :key="i"
                                                >
                                                    <span class="font-medium">{{ w.field }}:</span> {{ w.message }}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>

                <div class="mt-4 flex gap-3 items-center">
                    <button
                        v-if="!validationResult && !validating"
                        @click="validateExcelData"
                        class="bg-blue-600 text-white px-6 py-2 rounded-md font-medium hover:bg-blue-700"
                    >
                        Valida dati
                    </button>

                    <button
                        @click="openSaveNoteModal"
                        :disabled="uploading || !excelData.rows.length || !validationResult || hasBlockingErrors"
                        class="bg-green-600 text-white px-6 py-2 rounded-md font-medium disabled:opacity-50"
                    >
                        {{ uploading ? $t('gm.carica_excel.saving') : $t('gm.carica_excel.save_data') }}
                    </button>

                    <span v-if="!validationResult && !validating" class="text-sm text-gray-400 italic">
                        Valida i dati prima di salvare
                    </span>

                    <span v-if="hasBlockingErrors" class="text-sm text-red-500 italic">
                        Correggere gli errori prima di salvare
                    </span>
                </div>
            </div>

            <div v-if="files.length" class="mt-8 pb-6">
                <div class="flex items-center justify-between mb-4">
                    <div>
                        <h2 class="text-lg font-semibold text-gray-900">
                            {{ $t('gm.carica_excel.uploaded_files') }}
                        </h2>

                        <p class="text-sm text-gray-500 mt-1">
                            File caricati e righe importate
                        </p>
                    </div>

                    <span class="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-100">
                        {{ files.length }} file
                    </span>
                </div>

                <div class="space-y-4">
                    <div
                        v-for="f in files"
                        :key="f.id"
                        class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-200 hover:shadow-md"
                    >
                        <!-- HEADER ACCORDION -->
                        <div
                            role="button"
                            tabindex="0"
                            @click="toggleAccordion(f.id)"
                            @keydown.enter.prevent="toggleAccordion(f.id)"
                            @keydown.space.prevent="toggleAccordion(f.id)"
                            :class="[
                                'group w-full flex items-center justify-between gap-4 px-5 py-4 text-left transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset',
                                openAccordions[f.id] ? 'bg-blue-50/60 border-b border-blue-100' : 'hover:bg-gray-50'
                            ]"
                        >
                            <div class="flex items-center gap-4 min-w-0">
                                <!-- ICONA FRECCIA -->
                                <div
                                    :class="[
                                        'flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
                                        openAccordions[f.id]
                                            ? 'bg-blue-100 text-blue-700'
                                            : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                                    ]"
                                >
                                    <svg
                                        :class="[
                                            'w-4 h-4 transition-transform duration-200',
                                            openAccordions[f.id] ? 'rotate-90' : ''
                                        ]"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M9 5l7 7-7 7"
                                        />
                                    </svg>
                                </div>

                                <!-- INFO FILE -->
                                <div class="min-w-0">
                                    <div class="flex items-center gap-2 min-w-0">
                                        <p class="font-semibold text-gray-900 truncate">
                                            {{ f.file_name }}
                                        </p>

                                        <span
                                            v-if="openAccordions[f.id]"
                                            class="hidden sm:inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700"
                                        >
                                            Aperto
                                        </span>
                                    </div>

                                    <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                                        <span class="inline-flex items-center gap-1">
                                            <i class="fa fa-table text-gray-400"></i>
                                            {{ f.rows_count }} righe
                                        </span>

                                        <span class="hidden sm:inline text-gray-300">•</span>

                                        <span class="inline-flex items-center gap-1">
                                            <i class="fa fa-calendar text-gray-400"></i>
                                            {{ formatDate(f.created_at) }}
                                        </span>


                                        <template v-if="f.note || f.Note">
                                            <span class="hidden sm:inline text-gray-300">•</span>

                                            <span class="inline-flex items-center gap-1 text-blue-600">
                                                <i class="fa fa-sticky-note text-blue-400"></i>
                                                {{ f.note ? f.note : '' }}
                                            </span>
                                        </template>
                                    </div>
                                </div>
                            </div>

                            <!-- AZIONI -->
                            <div class="flex items-center gap-2 flex-shrink-0">
                                <button
                                    type="button"
                                    @click.stop="stampaEtichette(f.id)"
                                    class="inline-flex items-center gap-1.5 rounded-lg bg-yellow-50 px-3 py-2 text-sm font-medium text-yellow-700 ring-1 ring-inset ring-yellow-200 transition-colors hover:bg-yellow-100 hover:text-yellow-800"
                                >
                                    <i class="fa fa-print"></i>
                                    <span class="hidden sm:inline">Stampa Etichette</span>
                                </button>

                                <button
                                    type="button"
                                    @click.stop="openPrintNoteModal(f)"
                                    class="inline-flex items-center gap-1.5 rounded-lg bg-yellow-50 px-3 py-2 text-sm font-medium text-yellow-700 ring-1 ring-inset ring-yellow-200 transition-colors hover:bg-yellow-100 hover:text-yellow-800"
                                >
                                    <v-icon name="md-localshipping-twotone"></v-icon>
                                    <span class="hidden sm:inline">Stampa Bilico </span>
                                </button>
                                <template v-if="$page.props.isAdminOrSuperAdmin">
                                    <button
                                        type="button"
                                        @click.stop="deleteFile(f.id)"
                                        class="inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 ring-1 ring-inset ring-red-200 transition-colors hover:bg-red-100 hover:text-red-800"
                                    >
                                        <i class="fa fa-trash"></i>
                                        <span class="hidden sm:inline">{{ $t('gm.carica_excel.delete') }}</span>
                                    </button>
                                </template>
                            </div>
                        </div>

                        <!-- CONTENUTO ACCORDION -->
                        <Transition
                            enter-active-class="transition duration-200 ease-out"
                            enter-from-class="opacity-0 -translate-y-1"
                            enter-to-class="opacity-100 translate-y-0"
                            leave-active-class="transition duration-150 ease-in"
                            leave-from-class="opacity-100 translate-y-0"
                            leave-to-class="opacity-0 -translate-y-1"
                        >
                            <div
                                v-show="openAccordions[f.id]"
                                class="bg-white"
                            >
                                <div class="p-5">
                                    <div
                                        v-if="f.note || f.Note"
                                        class="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3"
                                    >
                                        <p class="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-1">
                                            Nota
                                        </p>

                                        <p class="text-sm text-blue-900 whitespace-pre-line">
                                            {{ f.note || f.Note }}
                                        </p>
                                    </div>

                                    <div
                                        v-if="f.rows.length"
                                        class="overflow-hidden rounded-lg border border-gray-200"
                                    >
                                        <div class="overflow-x-auto">
                                            <table class="min-w-full border-collapse">
                                                <thead>
                                                    <tr class="bg-gray-50">
                                                        <th
                                                            v-for="h in f.headers"
                                                            :key="h"
                                                            class="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 border-b border-gray-200"
                                                        >
                                                            {{ h }}
                                                        </th>
                                                    </tr>
                                                </thead>

                                                <tbody class="divide-y divide-gray-100 bg-white">
                                                    <tr
                                                        v-for="(row, index) in f.rows"
                                                        :key="index"
                                                        class="transition-colors hover:bg-blue-50/40"
                                                    >
                                                        <td
                                                            v-for="h in f.headers"
                                                            :key="h"
                                                            class="whitespace-nowrap px-4 py-2.5 text-sm text-gray-700"
                                                        >
                                                            {{ row[h] }}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div
                                        v-else
                                        class="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center"
                                    >
                                        <i class="fa fa-inbox text-2xl text-gray-300 mb-2"></i>

                                        <p class="text-sm text-gray-500 italic">
                                            {{ $t('gm.carica_excel.no_rows') }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Transition>
                    </div>
                </div>
            </div>
        </div>

        <!-- MODAL NOTA -->
        <Transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="noteModal.visible"
                class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4"
                @click.self="closeNoteModal"
            >
                <div class="w-full max-w-lg rounded-xl bg-white shadow-xl">
                    <div class="border-b border-gray-200 px-6 py-4">
                        <h3 class="text-lg font-semibold text-gray-900">
                            {{ noteModal.mode === 'save' ? 'Nota file Excel' : 'Nota per stampa lettura barcode' }}
                        </h3>

                        <p class="mt-1 text-sm text-gray-500">
                            Inserisci una nota da associare al file.
                        </p>
                    </div>

                    <div class="px-6 py-4">
                        <label class="mb-2 block text-sm font-medium text-gray-700">
                            Nota
                        </label>

                        <textarea
                            v-model="noteModal.note"
                            rows="5"
                            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            placeholder="Inserisci una nota..."
                        ></textarea>
                    </div>

                    <div class="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
                        <button
                            type="button"
                            @click="closeNoteModal"
                            :disabled="noteModal.loading"
                            class="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                        >
                            Annulla
                        </button>

                        <button
                            type="button"
                            @click="confirmNoteModal"
                            :disabled="noteModal.loading"
                            class="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                        >
                            {{ noteModal.loading ? 'Conferma in corso...' : 'Conferma' }}
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Loader from '@/Components/Loader.vue';
import Pagination from '@/Components/Pagination.vue';
import axios from 'axios';
import {
    ACCEPTED_EXTENSIONS,
    MAX_EXCEL_FILE_SIZE,
    MAX_EXCEL_FILE_SIZE_MB,
    getExtension,
    isMimeAcceptable,
    parseSpreadsheetBuffer,
} from '@/utils/excelParser';

function getValidationChunkSize(totalRows) {
    if (totalRows <= 10) return 5;
    if (totalRows <= 50) return 10;
    if (totalRows <= 100) return 20;
    return 50;
}

export default {
    props: {
        files: { type: Array, default: () => [] },
    },

    components: {
        Link,
        BreadCrumbs,
        Pagination,
        Loader,
    },

    data() {
        return {
            navigation: [
                { name: 'Dashboard', href: route('dashboard') },
                { name: this.$t('modules.gestione-magazzino'), href: route('modules.home', 'gestione-magazzino') },
                { name: this.$t('modules.carica-excel'), href: '#' },
            ],

            dragOver: false,
            selectedFile: null,

            excelData: {
                headers: [],
                rows: [],
            },

            uploading: false,
            validationResult: null,
            validating: false,
            hasBlockingErrors: false,
            openAccordions: {},
            isLoading: false,
            loadingText: '',
            validated: false,

            validationProgress: {
                processed: 0,
                total: 0,
            },

            noteModal: {
                visible: false,
                mode: null, // save | print
                note: '',
                fileId: null,
                loading: false,
            },
        };
    },

    methods: {
        handleFileDrop(e) {
            this.dragOver = false;

            const files = e.dataTransfer.files;

            if (files.length > 0) {
                this.validateAndSetFile(files[0]);
            }
        },

        handleFileSelect(e) {
            const files = e.target.files;

            if (files.length > 0) {
                this.validateAndSetFile(files[0]);
            }
        },

        validateAndSetFile(file) {
            const extension = getExtension(file.name);

            if (extension === 'xls') {
                alert(this.$t('gm.carica_excel.xls_not_supported'));
                return;
            }

            if (!ACCEPTED_EXTENSIONS.includes(extension)) {
                alert(this.$t('gm.carica_excel.invalid_file_type'));
                return;
            }

            if (file.size > MAX_EXCEL_FILE_SIZE) {
                alert(this.$t('gm.carica_excel.file_too_large', { size: MAX_EXCEL_FILE_SIZE_MB }));
                return;
            }

            if (!isMimeAcceptable(file.type)) {
                console.warn(`Tipo MIME non riconosciuto per il file selezionato: "${file.type}". Verrà verificato in fase di lettura.`);
            }

            this.selectedFile = file;
        },

        resetSelection() {
            this.selectedFile = null;

            this.excelData = {
                headers: [],
                rows: [],
            };

            this.validationResult = null;
            this.hasBlockingErrors = false;
            this.validating = false;

            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }

            this.validated = false;
        },

        async elaboraExcel(file) {
            if (this.isLoading) {
                return;
            }

            this.isLoading = true;
            this.loadingText = 'Lettura del file in corso...';

            try {
                const extension = getExtension(file.name);

                if (extension === 'xls') {
                    alert(this.$t('gm.carica_excel.xls_not_supported'));
                    return;
                }

                if (!ACCEPTED_EXTENSIONS.includes(extension)) {
                    alert(this.$t('gm.carica_excel.invalid_file_type'));
                    return;
                }

                if (file.size > MAX_EXCEL_FILE_SIZE) {
                    alert(this.$t('gm.carica_excel.file_too_large', { size: MAX_EXCEL_FILE_SIZE_MB }));
                    return;
                }

                const buffer = await file.arrayBuffer();
                const jsonData = await parseSpreadsheetBuffer(buffer, extension);

                if (jsonData.length > 0) {
                    this.excelData.headers = Object.keys(jsonData[0]);
                    this.excelData.rows = jsonData;
                    this.validationResult = null;
                    this.hasBlockingErrors = false;

                    await this.validateExcelData();

                    this.validated = true;
                } else {
                    this.resetSelection();
                    alert(this.$t('gm.carica_excel.no_rows'));
                }
            } catch (err) {
                console.error('Errore durante la lettura del file:', err);

                this.excelData = { headers: [], rows: [] };
                this.validationResult = null;
                this.hasBlockingErrors = false;
                this.validated = false;

                alert(this.$t('gm.carica_excel.read_error'));
            } finally {
                this.isLoading = false;
            }
        },

        async validateExcelData() {
            if (!this.excelData.rows.length || this.validating) return;

            this.validating = true;
            this.isLoading = true;

            const totalRows = this.excelData.rows.length;
            this.validationProgress = { processed: 0, total: totalRows };
            this.loadingText = `Controllo dei dati in corso (0/${totalRows})...`;

            const aggregated = {
                valid: true,
                has_blocking_errors: false,
                summary: { total_rows: totalRows, blocking_errors_count: 0, warnings_count: 0 },
                rows: [],
            };

            const chunkSize = getValidationChunkSize(totalRows);

            try {
                for (let offset = 0; offset < totalRows; offset += chunkSize) {
                    const chunk = this.excelData.rows.slice(offset, offset + chunkSize);

                    const { data } = await axios.post(route('modules.gm.caricaexcel.validate'), {
                        file_name: this.selectedFile?.name ?? 'unknown.xlsx',
                        headers: this.excelData.headers,
                        rows: chunk,
                    });

                    aggregated.has_blocking_errors = aggregated.has_blocking_errors || !!data.has_blocking_errors;
                    aggregated.valid = aggregated.valid && !!data.valid;
                    aggregated.summary.blocking_errors_count += data.summary?.blocking_errors_count ?? 0;
                    aggregated.summary.warnings_count += data.summary?.warnings_count ?? 0;

                    data.rows.forEach(validatedRow => {
                        const rowIndex = offset + validatedRow.row_index;

                        aggregated.rows.push({ ...validatedRow, row_index: rowIndex });

                        if (!this.excelData.rows[rowIndex]) return;

                        const supplierOrder = validatedRow.normalized?.supplier_order;
                        if (supplierOrder) {
                            this.excelData.rows[rowIndex]['Ordine Fornitore'] = supplierOrder;
                        }

                        const bin = validatedRow.normalized?.bin;
                        if (bin && !this.excelData.rows[rowIndex]['Ubicazione']) {
                            this.excelData.rows[rowIndex]['Ubicazione'] = bin;
                        }
                    });

                    this.validationProgress.processed = Math.min(offset + chunk.length, totalRows);
                    this.loadingText = `Controllo dei dati in corso (${this.validationProgress.processed}/${totalRows})...`;
                }

                this.validationResult = aggregated;
                this.hasBlockingErrors = aggregated.has_blocking_errors;
            } catch (e) {
                alert('Errore durante la validazione: ' + (e.response?.data?.message ?? e.message));
            } finally {
                this.validating = false;
                this.isLoading = false;
                this.validationProgress = { processed: 0, total: 0 };
            }
        },

        openSaveNoteModal() {
            if (!this.excelData.rows.length || this.uploading) return;
            if (!this.validationResult || this.hasBlockingErrors) return;

            this.noteModal = {
                visible: true,
                mode: 'save',
                note: '',
                fileId: null,
                loading: false,
            };
        },

        openPrintNoteModal(file) {
            if (!file?.id) return;

            this.noteModal = {
                visible: true,
                mode: 'print',
                note: file.note ?? file.Note ?? '',
                fileId: file.id,
                loading: false,
            };
        },

        closeNoteModal() {
            if (this.noteModal.loading) return;

            this.noteModal = {
                visible: false,
                mode: null,
                note: '',
                fileId: null,
                loading: false,
            };
        },

        async confirmNoteModal() {
            if (this.noteModal.mode === 'save') {
                await this.uploadExcelData(this.noteModal.note);
                return;
            }

            if (this.noteModal.mode === 'print') {
                await this.saveNoteAndPrint();
            }
        },

        async uploadExcelData(note = '') {
            if (!this.excelData.rows.length || this.uploading) return;
            if (!this.validationResult || this.hasBlockingErrors) return;

            if (this.validationResult?.summary?.warnings_count > 0) {
                const proceed = confirm('Sono presenti warning non bloccanti. Vuoi procedere comunque?');

                if (!proceed) return;
            }

            this.uploading = true;
            this.noteModal.loading = true;

            try {
                await axios.post(route('modules.gm.caricaexcel.store'), {
                    file_name: this.selectedFile?.name ?? 'unknown.xlsx',
                    headers: this.excelData.headers,
                    rows: this.excelData.rows,
                    validation: this.validationResult,
                    note,
                });

                this.noteModal = {
                    visible: false,
                    mode: null,
                    note: '',
                    fileId: null,
                    loading: false,
                };

                this.resetSelection();

                router.reload({
                    only: ['files'],
                    preserveScroll: true,
                });
            } catch (e) {
                alert(this.$t('gm.carica_excel.save_error') + (e.response?.data?.message ?? e.message));
            } finally {
                this.uploading = false;
                this.isLoading = false;
                this.noteModal.loading = false;
            }
        },

        async saveNoteAndPrint() {
            if (!this.noteModal.fileId) return;

            this.noteModal.loading = true;

            try {
                const fileId = this.noteModal.fileId;

                await axios.patch(route('modules.gm.caricaexcel.update-note', {
                    id: fileId,
                }), {
                    note: this.noteModal.note,
                });

                this.noteModal = {
                    visible: false,
                    mode: null,
                    note: '',
                    fileId: null,
                    loading: false,
                };

                router.reload({
                    only: ['files'],
                    preserveScroll: true,
                });

                window.open(route('modules.gm.caricaexcel.print', { id: fileId }), '_blank');
            } catch (e) {
                alert('Errore durante il salvataggio della nota: ' + (e.response?.data?.message ?? e.message));
            } finally {
                this.noteModal.loading = false;
            }
        },

        async deleteFile(id) {
            if (!confirm(this.$t('gm.carica_excel.confirm_delete'))) return;

            try {
                await axios.delete(route('modules.gm.caricaexcel.delete', { id }));

                router.reload({
                    only: ['files'],
                    preserveScroll: true,
                });
            } catch (e) {
                alert(this.$t('gm.carica_excel.delete_error') + (e.response?.data?.message ?? e.message));
            }
        },

        stampaEtichette(id) {
            const file = this.files.find(f => f.id === id);

            if (!file || !file.headers.includes('Stampa Barcode')) {
                alert('La colonna "Stampa Barcode" non è presente nel file importato. Aggiungila all\'Excel e reimporta il file per abilitare la stampa delle etichette.');
                return;
            }

            window.open(route('modules.gm.caricaexcel.etichette', { id }), '_blank');
        },

        stampaFile(id) {
            window.open(route('modules.gm.caricaexcel.print', { id }), '_blank');
        },

        formatDate(iso) {
            if (!iso) return '';

            const d = new Date(iso);

            return isNaN(d) ? iso : d.toLocaleString('it-IT');
        },

        getRowValidation(index) {
            if (!this.validationResult) return null;

            return this.validationResult.rows.find(r => r.row_index === index) ?? null;
        },

        getRowClass(index) {
            const v = this.getRowValidation(index);

            if (!v) return 'hover:bg-gray-50 transition-colors';
            if (v.errors.length > 0) return 'bg-red-50';
            if (v.warnings.length > 0) return 'bg-yellow-50';

            return 'bg-green-50';
        },

        getRowDetailClass(index) {
            const v = this.getRowValidation(index);

            if (!v) return '';
            if (v.errors.length > 0) return 'bg-red-50';
            if (v.warnings.length > 0) return 'bg-yellow-50';

            return 'bg-green-50';
        },

        getRowStatusIcon(index) {
            const v = this.getRowValidation(index);

            if (!v) return '';
            if (v.errors.length > 0) return '❌';
            if (v.warnings.length > 0) return '⚠️';

            return '✅';
        },

        hasRowDetails(index) {
            const v = this.getRowValidation(index);

            if (!v) return false;

            return v.errors.length > 0 || v.warnings.length > 0;
        },

        toggleAccordion(id) {
            this.openAccordions = {
                ...this.openAccordions,
                [id]: !this.openAccordions[id],
            };
        },
    },

    mounted() {

    },
};
</script>